import styles from './styles.module.scss';
import { useEffect, useState } from 'react';
import Typography from '@components/common/typography';
import PaymentsTable from '@sections/send_and_request/schedule_payments/payments_table';
import { useTranslation } from 'react-i18next';
import { useApiQuery } from '@hooks/api/useApiQuery';
import { apiScheduleService } from '@api';
import { ENewNotificationType, EFundsOperation } from '@enum';
import Pagination from '@components/pagination';
import { motion } from 'framer-motion';
import { pageAnimation } from '@const/animation';
import { EScheduleFrequency, EScheduleStatus, TSchedule } from '@xeppt/xeppt-sdk/types/schedule';
import { ETransactionAction, ETransactionMethod } from '@xeppt/xeppt-sdk/types';
import { useNavigate } from 'react-router-dom';
import { routes } from '@const/routes';
import { useDebounceValue } from 'usehooks-ts';
import Button from '@components/common/button';
import { useNavigationContext } from '@hooks/context/useNavigationContext';
import { useLocales } from '@hooks/helpers/useLocales';

const SchedulePaymentsSection = () => {
    const { t } = useTranslation('sections', {
        keyPrefix: 'send_and_request.schedule_payments'
    });
    const { submitLocale } = useLocales();
    const navigate = useNavigate();
    const [search, setSearch] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useDebounceValue(search, 500);
    const [activeTab, setActiveTab] = useState('payments');
    const [isFirstRender, setIsFirstRender] = useState(true);
    const [filter, setFilter] = useState<{
        frequency?: EScheduleFrequency[];
        status?: EScheduleStatus[];
        method?: ETransactionMethod[];
        action?: ETransactionAction[];
    }>({
        frequency: [],
        status: [],
        method: [],
        action: [ETransactionAction.TRANSFER, ETransactionAction.WITHDRAW]
    });
    const [page, setPage] = useState(0);
    const [pageSize, setPageSize] = useState(10);
    const { handleBack } = useNavigationContext();

    const {
        data: paymentsList,
        handleRequest: refetchPaymentsList,
        isLoading
    } = useApiQuery({
        method: () => {
            return apiScheduleService.listSchedules(pageSize, page + 1, {
                frequency: (filter.frequency || [])?.length > 0 ? filter.frequency : undefined,
                status: (filter.status || [])?.length > 0 ? filter.status : undefined,
                method:
                    (filter.method || [])?.length > 0
                        ? filter.method
                        : [
                              ETransactionMethod.WALLET,
                              ETransactionMethod.EFT,
                              ETransactionMethod.EPS
                          ],
                action: (filter.action || [])?.length > 0 ? filter.action : undefined,
                info: debouncedSearch.length >= 3 ? debouncedSearch : undefined
            });
        },
        isInitialRequest: false,
        isInitialLoading: true,
        deps: [filter, page, pageSize],
        notificationType: ENewNotificationType.BILL
    });

    useEffect(() => {
        if (!isFirstRender) {
            setFilter({
                ...filter,
                action:
                    activeTab === 'payments'
                        ? [ETransactionAction.TRANSFER, ETransactionAction.WITHDRAW]
                        : [ETransactionAction.REQUEST, ETransactionAction.DEPOSIT]
            });
        }
        setIsFirstRender(false);
    }, [activeTab]);

    useEffect(() => {
        setDebouncedSearch(search);
    }, [search]);

    useEffect(() => {
        if (debouncedSearch.length >= 3) {
            refetchPaymentsList();
        } else if (debouncedSearch.length === 0) {
            refetchPaymentsList();
        }
    }, [debouncedSearch]);

    const onEdit = (schedule: TSchedule) => {
        if (schedule.method === ETransactionMethod.WALLET) {
            if (schedule.action === ETransactionAction.TRANSFER) {
                navigate(
                    `${routes.send_and_request_send}?type=${EFundsOperation.WALLET_CREDIT_TRANSFER}&id=${schedule.id}`
                );
            } else if (schedule.action === ETransactionAction.REQUEST) {
                navigate(
                    `${routes.send_and_request_request}?type=${EFundsOperation.WALLET_DEBIT_REQUEST}&id=${schedule.id}`
                );
            }
        } else if (schedule.method === ETransactionMethod.EFT) {
            if (schedule.action === ETransactionAction.WITHDRAW) {
                navigate(
                    `${routes.send_and_request_send}?type=${EFundsOperation.EFT_CREDIT_WITHDRAWAL}&id=${schedule.id}`
                );
            } else if (schedule.action === ETransactionAction.DEPOSIT) {
                navigate(
                    `${routes.send_and_request_request}?type=${EFundsOperation.EFT_DEBIT_DEPOSIT}&id=${schedule.id}`
                );
            }
        } else if (schedule.method === ETransactionMethod.EPS) {
            if (schedule.action === ETransactionAction.DEPOSIT) {
                navigate(
                    `${routes.send_and_request_request}?type=${EFundsOperation.EPS_DEBIT_DEPOSIT}&id=${schedule.id}`
                );
            }
        }
    };

    return (
        <motion.div {...pageAnimation} className={styles.main_wrapper}>
            <div className={styles.wrapper}>
                <div className={styles.content}>
                    <div className={styles.header}>
                        <Button
                            className={styles.back}
                            leftIcon="arrow_left"
                            variant="text-gray"
                            onClick={() => handleBack(routes.send_and_request)}>
                            {submitLocale('back')}
                        </Button>
                        <Typography variant="h4">{t('title')}</Typography>
                    </div>
                </div>
                <PaymentsTable
                    refetchPaymentsList={refetchPaymentsList}
                    paymentsList={paymentsList?.data || []}
                    onFilterChange={(filter) => setFilter(filter)}
                    filter={filter}
                    activeTab={activeTab}
                    onActiveTabChange={(val) => setActiveTab(val)}
                    onEdit={onEdit}
                    onSearch={(val) => setSearch(val)}
                    search={search}
                    isLoading={isLoading}
                />
                {(paymentsList?.pages_total || 0) > 1 && (
                    <Pagination
                        pageCount={paymentsList?.pages_total || 0}
                        onPageChange={(val) => setPage(val)}
                        pageSize={pageSize}
                        onPageSizeChange={(val) => setPageSize(val)}
                    />
                )}
            </div>
        </motion.div>
    );
};

export default SchedulePaymentsSection;
